import { createContext, ReactNode, useContext } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { LeaguestarState } from '../state'
import { Round } from '../matches/api'

/**
 * Scoped override for `useCompetitionId()`. Wrap a subtree (e.g. the dashboard
 * editor canvas or the dashboard-by-id preview) in this provider to fix the
 * competition for everything inside, independent of URL/global state. The hook
 * consults this context first, so widgets resolve to the dashboard's bound
 * competition without depending on whatever competition the user last
 * navigated to in the main app.
 */
const CompetitionIdContext = createContext<number | null>(null)

export function CompetitionIdProvider({
  competitionId,
  children,
}: {
  competitionId: number | null
  children: ReactNode
}) {
  return (
    <CompetitionIdContext.Provider value={competitionId}>
      {children}
    </CompetitionIdContext.Provider>
  )
}

export function useCompetitionId(): number {
  const fromContext = useContext(CompetitionIdContext)
  const { competitionId } = useParams()
  const competition_id = LeaguestarState.useState(
    (state) => state.competition_id
  )
  if (fromContext != null && fromContext !== 0) {
    return fromContext
  }
  if (competition_id != null && competition_id !== 0) {
    return competition_id
  } else if (competitionId) {
    return parseInt(competitionId, 10)
  } else {
    return 0
  }
}

export function useRoundDivisionParams(rounds: Round[]): {
  currentRound: number | null
  currentDivision: number | null
  setRound: (round: number | null) => void
  setDivision: (division: number | null) => void
} {
  const [searchParams, setSearchParams] = useSearchParams()

  const roundParam = searchParams.get('round')
  const divisionParam = searchParams.get('division')

  const parsedRound = roundParam ? Number(roundParam) : null
  const roundExists =
    parsedRound !== null && rounds.some((r) => r.round_number === parsedRound)

  const latestRound = rounds.find((r) => r.is_latest) ?? rounds[0] ?? null

  const currentRound = roundExists
    ? parsedRound
    : (latestRound?.round_number ?? null)
  const currentDivision = divisionParam ? Number(divisionParam) : null

  const setRound = (round: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (round !== null) {
          next.set('round', round.toString())
        } else {
          next.delete('round')
        }
        return next
      },
      { replace: true }
    )
  }

  const setDivision = (division: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (division !== null) {
          next.set('division', division.toString())
        } else {
          next.delete('division')
        }
        return next
      },
      { replace: true }
    )
  }

  return { currentRound, currentDivision, setRound, setDivision }
}
